import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { useTheme } from '@mui/material';
import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { useEffect } from 'react';
import { mapScreenTypeToSectionType } from '#helpers/enumMappers.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { BackdropImage } from '../BackdropImage/index.tsx';
import { ImageCarousel } from '../ImageCarousel/index.tsx';
import { type MovieBackdropsData, type TvBackdropsData, formatBackdrops } from './helpers/formatBackdrops.ts';
import { type Props } from './types.ts';

const queries = {
  [ScreenType.Movie]: "\nquery GetMovieBackdrops(\n  $id: ID!\n) {\n  movie(id: $id) {\n    ...MovieBackdrops\n  }\n}\n\nfragment MovieBackdrops on Movie {\n  backdrops {\n    aspectRadio\n    filePath\n    fileType\n    height\n    width\n  }\n}\n",
  [ScreenType.Tv]: "\nquery GetTvBackdrops(\n  $id: ID!\n) {\n  tv(id: $id) {\n    ...TvBackdrops\n  }\n}\n\nfragment TvBackdrops on Tv {\n  backdrops {\n    aspectRadio\n    filePath\n    fileType\n    height\n    width\n  }\n}\n",
};

export const BackdropsCarousel = ({ children, id, title, type }: Props) => {
  const { config } = useToolbelt();
  const theme = useTheme();
  const query = queries[type];

  const { execute, loading, result } = useQuery<MovieBackdropsData | TvBackdropsData>(query, {
    loading: true,
  });

  useEffect(() => {
    void execute({ variables: { id } }, { data: { initiator: 'BackdropsCarousel' } });
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  const backdrops = formatBackdrops(result?.data, config);

  if (!loading && backdrops.length === 0) {
    return;
  }

  const imageCarousel = (
    <ImageCarousel
      images={backdrops}
      renderImage={props => (
        <BackdropImage
          pictureContainerSx={{
            [theme.breakpoints.between('md', 'lg')]: {
              paddingBottom: '50%',
            },
            [theme.breakpoints.down('md')]: {
              paddingBottom: '56%',
            },
            paddingBottom: '45%',
          }}
          title={title}
          type={mapScreenTypeToSectionType(type)}
          {...props}
        />
      )}
    />
  );

  return children ? children(imageCarousel) : imageCarousel;
};
